import { useState, useEffect, useCallback } from 'react';
import GameBoard from './components/GameBoard';
import GameSetup from './components/GameSetup';
import ShipPlacement from './components/ShipPlacement';
import MathModal from './components/MathModal';
import GameStatus from './components/GameStatus';
import './App.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function App() {
  const [gameState, setGameState] = useState(null);
  const [gameCode, setGameCode] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [placingShip, setPlacingShip] = useState(null);
  const [error, setError] = useState(null);
  const [playerGames, setPlayerGames] = useState([]);

  const generateFingerprint = useCallback((forceNew = false) => {
    // For testing: Check if we want to force a new identity (e.g., ?guest=true in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const isGuestMode = urlParams.get('guest') === 'true';
    
    if (isGuestMode || forceNew) {
      // Generate a new guest ID for this tab only
      const guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem('playerFingerprint', guestId);
      console.log('Generated guest fingerprint for testing:', guestId);
      return guestId;
    }
    
    // Check sessionStorage first (for this specific tab)
    const tabFingerprint = sessionStorage.getItem('playerFingerprint');
    if (tabFingerprint) {
      console.log('Using existing tab fingerprint:', tabFingerprint);
      return tabFingerprint;
    }
    
    // Check localStorage for persistent player ID (shared across tabs)
    let persistentId = localStorage.getItem('playerFingerprint');
    if (!persistentId) {
      // Generate a new persistent ID only if one doesn't exist
      persistentId = `player_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem('playerFingerprint', persistentId);
      console.log('Generated new persistent fingerprint:', persistentId);
    } else {
      console.log('Using existing persistent fingerprint:', persistentId);
    }
    
    // Store in sessionStorage for this tab
    sessionStorage.setItem('playerFingerprint', persistentId);
    return persistentId;
  }, []);

  useEffect(() => {
    const fingerprint = generateFingerprint();
    setPlayerId(fingerprint);
    console.log('Player fingerprint:', fingerprint);

    // Check if game code is in URL
    const pathGameCode = window.location.pathname.slice(1).toUpperCase();
    if (pathGameCode && pathGameCode.match(/^[A-Z0-9]{6}$/)) {
      console.log('Found game code in URL:', pathGameCode);
      setGameCode(pathGameCode);
      sessionStorage.setItem('currentGameCode', pathGameCode);
    } else {
      // Otherwise check sessionStorage
      const savedGameCode = sessionStorage.getItem('currentGameCode');
      if (savedGameCode) {
        setGameCode(savedGameCode);
        console.log('Restored game code:', savedGameCode);
      }
    }
  }, [generateFingerprint]);

  // Fetch player's games when on home page
  useEffect(() => {
    if (!playerId || gameCode) return; // Only fetch when on home page

    const fetchPlayerGames = async () => {
      try {
        const response = await fetch(`${API_URL}/api/player/${playerId}/games`);
        if (response.ok) {
          const games = await response.json();
          setPlayerGames(games);
          console.log('Player games:', games);
        }
      } catch (err) {
        console.error('Failed to fetch player games:', err);
      }
    };

    fetchPlayerGames();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchPlayerGames, 5000);
    return () => clearInterval(interval);
  }, [playerId, gameCode]);

  useEffect(() => {
    if (!gameCode || !playerId) return;

    const pollGame = async () => {
      try {
        const response = await fetch(`${API_URL}/api/game/${gameCode}`);
        if (response.ok) {
          const game = await response.json();
          
          // Check if player is not in the game and auto-join
          if (!game.players[playerId] && !game.guest) {
            console.log('Player not in game, attempting to join...');
            const joinResponse = await fetch(`${API_URL}/api/game/${gameCode}/join`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ playerFingerprint: playerId })
            });
            
            if (joinResponse.ok) {
              console.log('Successfully joined game via URL');
              // Fetch updated game state
              const updatedResponse = await fetch(`${API_URL}/api/game/${gameCode}`);
              if (updatedResponse.ok) {
                const updatedGame = await updatedResponse.json();
                setGameState(updatedGame);
                return;
              }
            }
          }
          
          setGameState(prevState => {
            // Force re-render if guest status changed
            if (!prevState || prevState.guest !== game.guest) {
              console.log('Guest status changed:', prevState?.guest, '->', game.guest);
            }
            return game;
          });
          console.log('Game state updated:', { 
            phase: game.phase, 
            host: game.host, 
            guest: game.guest,
            players: Object.keys(game.players)
          });
        } else if (response.status === 404) {
          sessionStorage.removeItem('currentGameCode');
          setGameCode(null);
          setGameState(null);
        }
      } catch (err) {
        console.error('Failed to fetch game state:', err);
      }
    };

    pollGame();
    const interval = setInterval(pollGame, 2000);
    return () => clearInterval(interval);
  }, [gameCode, playerId]);

  const createGame = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerFingerprint: playerId })
      });

      if (response.ok) {
        const data = await response.json();
        setGameCode(data.gameCode);
        sessionStorage.setItem('currentGameCode', data.gameCode);
        // Update URL to include game code
        window.history.pushState({}, '', `/${data.gameCode}`);
        console.log('Created game:', data.gameCode);
      }
    } catch (err) {
      setError('Failed to create game');
    }
  };

  const createComputerGame = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/create-vs-computer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerFingerprint: playerId })
      });

      if (response.ok) {
        const data = await response.json();
        setGameCode(data.gameCode);
        sessionStorage.setItem('currentGameCode', data.gameCode);
        window.history.pushState({}, '', `/${data.gameCode}`);
        console.log('Created computer game:', data.gameCode);
      }
    } catch (err) {
      setError('Failed to create computer game');
    }
  };

  const joinGame = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/game/${code}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerFingerprint: playerId })
      });

      if (response.ok) {
        const data = await response.json();
        setGameCode(code);
        sessionStorage.setItem('currentGameCode', code);
        // Update URL to include game code
        window.history.pushState({}, '', `/${code}`);
        console.log('Joined game:', code, 'Response:', data);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to join game');
      }
    } catch (err) {
      setError('Failed to join game');
    }
  };

  const startGame = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/${gameCode}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId })
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to start game');
      }
    } catch (err) {
      setError('Failed to start game');
    }
  };

  const handleCellClick = async (cell) => {
    if (!gameState || !playerId) return;

    if (gameState.phase === 'placement') {
      // Allow clicking any cell during ship placement
      if (placingShip) {
        if (selectedCells.includes(cell)) {
          setSelectedCells(selectedCells.filter(c => c !== cell));
        } else {
          setSelectedCells([...selectedCells, cell]);
        }
      }
    } else if (gameState.phase === 'battle' && gameState.currentTurn === playerId) {
      const player = gameState.players[playerId];
      if (!player.attacks.includes(cell)) {
        // Show math problem before allowing attack
        const problem = gameState.mathProblems[cell];
        setCurrentProblem({ cell, ...problem, isAttack: true });
      }
    }
  };

  const submitAnswer = async (answer) => {
    try {
      const response = await fetch(`${API_URL}/api/game/${gameCode}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          playerId, 
          cell: currentProblem.cell, 
          answer 
        })
      });

      const data = await response.json();
      
      if (data.correct) {
        // If this was an attack, perform the attack
        if (currentProblem.isAttack) {
          await attack(currentProblem.cell);
        }
        setCurrentProblem(null);
      } else {
        // Increment wrongAttempts to trigger shake animation on each wrong answer
        setCurrentProblem(prev => ({
          ...prev,
          incorrect: true,
          wrongAttempts: (prev.wrongAttempts || 0) + 1,
          correctAnswer: data.correctAnswer
        }));
      }
    } catch (err) {
      setError('Failed to submit answer');
    }
  };

  const placeShip = async () => {
    if (!placingShip || selectedCells.length === 0) return;

    try {
      const response = await fetch(`${API_URL}/api/game/${gameCode}/place-ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId,
          shipType: placingShip,
          cells: selectedCells
        })
      });

      if (response.ok) {
        setSelectedCells([]);
        setPlacingShip(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to place ship');
      }
    } catch (err) {
      setError('Failed to place ship');
    }
  };

  const autoPlaceShips = async () => {
    const SHIPS = [
      { type: 'carrier', size: 5 },
      { type: 'battleship', size: 4 },
      { type: 'cruiser', size: 3 },
      { type: 'submarine', size: 3 },
      { type: 'destroyer', size: 2 }
    ];

    const occupiedCells = new Set();
    const player = gameState.players[playerId];

    // Add already placed ships to occupied cells
    Object.values(player.ships).flat().forEach(cell => occupiedCells.add(cell));

    const shipsToPlace = SHIPS.filter(ship => !player.ships[ship.type]);

    for (const ship of shipsToPlace) {
      let placed = false;
      let attempts = 0;
      const maxAttempts = 100;

      while (!placed && attempts < maxAttempts) {
        attempts++;
        const isHorizontal = Math.random() < 0.5;
        const startRow = Math.floor(Math.random() * 12) + 1;
        const startCol = Math.floor(Math.random() * 12) + 1;

        const cells = [];
        let valid = true;

        for (let i = 0; i < ship.size; i++) {
          let row, col;
          if (isHorizontal) {
            row = startRow;
            col = startCol + i;
          } else {
            row = startRow + i;
            col = startCol;
          }

          if (row < 1 || row > 12 || col < 1 || col > 12) {
            valid = false;
            break;
          }

          const cell = `${String.fromCharCode(64 + col)}${row}`;
          if (occupiedCells.has(cell)) {
            valid = false;
            break;
          }
          cells.push(cell);
        }

        if (valid && cells.length === ship.size) {
          try {
            const response = await fetch(`${API_URL}/api/game/${gameCode}/place-ship`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                playerId,
                shipType: ship.type,
                cells
              })
            });

            if (response.ok) {
              cells.forEach(cell => occupiedCells.add(cell));
              placed = true;
            }
          } catch (err) {
            console.error('Failed to place ship:', err);
          }
        }
      }

      if (!placed) {
        setError(`Failed to auto-place ${ship.type}. Please place manually.`);
        return;
      }
    }

    setSelectedCells([]);
    setPlacingShip(null);
  };

  const setReady = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/${gameCode}/ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId })
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to set ready');
      }
    } catch (err) {
      setError('Failed to set ready');
    }
  };

  const attack = async (cell) => {
    try {
      const response = await fetch(`${API_URL}/api/game/${gameCode}/attack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, cell })
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to attack');
      }
    } catch (err) {
      setError('Failed to attack');
    }
  };

  const resumeGame = (code) => {
    setGameCode(code);
    sessionStorage.setItem('currentGameCode', code);
    window.history.pushState({}, '', `/${code}`);
  };

  if (!gameCode || !gameState) {
    return (
      <GameSetup
        onCreateGame={createGame}
        onCreateComputerGame={createComputerGame}
        onJoinGame={joinGame}
        onResumeGame={resumeGame}
        playerGames={playerGames}
        error={error}
      />
    );
  }

  const player = gameState.players[playerId];
  const isHost = gameState.host === playerId;
  const opponentId = isHost ? gameState.guest : gameState.host;
  const opponent = opponentId ? gameState.players[opponentId] : null;
  
  console.log('App Debug:', {
    playerId,
    gameHost: gameState.host,
    gameGuest: gameState.guest,
    isHost,
    opponentId,
    playerExists: !!player,
    opponentExists: !!opponent,
    allPlayers: Object.keys(gameState.players)
  });

  const handleBackToHome = () => {
    setGameCode(null);
    setGameState(null);
    sessionStorage.removeItem('currentGameCode');
    window.history.pushState({}, '', '/');
  };

  return (
    <div className="app">
      <header>
        <h1>Math Facts Battleship</h1>
        <div className="game-info">
          <button onClick={handleBackToHome} className="btn-home">← Home</button>
          <span>Game Code: <strong>{gameCode}</strong></span>
          <span>Phase: <strong>{gameState.phase}</strong></span>
          {gameState.isComputerGame ? (
            <span>Mode: <strong>vs Computer</strong></span>
          ) : (
            <>
              <span>You: <strong>{isHost ? 'Host' : 'Guest'}</strong></span>
              <span>Players: <strong>{Object.keys(gameState.players).length}/2</strong></span>
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <GameStatus 
        gameState={gameState}
        playerId={playerId}
        isHost={isHost}
        onStartGame={startGame}
      />

      {gameState.phase === 'placement' && (
        <ShipPlacement
          player={player}
          placingShip={placingShip}
          selectedCells={selectedCells}
          onSelectShip={setPlacingShip}
          onPlaceShip={placeShip}
          onSetReady={setReady}
          onAutoPlace={autoPlaceShips}
        />
      )}

      <div className="game-boards">
        <div className="board-container">
          <h2>Your Board</h2>
          <GameBoard
            gameState={gameState}
            playerId={playerId}
            isOwnBoard={true}
            selectedCells={selectedCells}
            onCellClick={handleCellClick}
          />
        </div>

        {opponent && (gameState.phase === 'battle' || gameState.phase === 'finished') && (
          <div className="board-container">
            <h2>{gameState.isComputerGame ? "Computer's Board" : "Opponent's Board"}</h2>
            <GameBoard
              gameState={gameState}
              playerId={playerId}
              isOwnBoard={false}
              selectedCells={[]}
              onCellClick={handleCellClick}
              showAllShips={gameState.phase === 'finished'}
            />
          </div>
        )}
      </div>

      {currentProblem && (
        <MathModal
          problem={currentProblem}
          onSubmit={submitAnswer}
          onClose={() => setCurrentProblem(null)}
        />
      )}
    </div>
  );
}

export default App;